import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PoolProvider } from './context/PoolContext';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import BottomTabBar from './components/BottomTabBar';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import Loading from './components/Loading';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import CreatePool from './pages/CreatePool';
import JoinPool from './pages/JoinPool';
import PoolDetail from './pages/PoolDetail';
import PoolPicks from './pages/PoolPicks';
import PoolLeaderboard from './pages/PoolLeaderboard';
import PoolSettings from './pages/PoolSettings';
import LiveLeaderboard from './pages/LiveLeaderboard';
import Admin from './pages/Admin';
import FAQ from './pages/FAQ';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Profile from './pages/Profile';
import Badges from './pages/Badges';
import LockSheet from './pages/LockSheet';
import CornerChat from './pages/CornerChat';
import { initPushIfReady } from './services/pushNotifications';
import { Capacitor } from '@capacitor/core';
import './index.css';
import MarketingShell, { isMarketingPath } from './components/marketing/MarketingShell';

function PushBootstrapper() {
  const { isAuthenticated } = useAuth();
  React.useEffect(() => {
    if (isAuthenticated) initPushIfReady();
  }, [isAuthenticated]);
  return null;
}

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public route - redirects to dashboard if already logged in
// Preserves redirect parameter to avoid race condition after login
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const redirectUrl = searchParams.get('redirect');

  if (loading) {
    return <Loading />;
  }

  if (isAuthenticated) {
    // If there's a redirect URL in the query params, honor it
    if (redirectUrl) {
      return <Navigate to={redirectUrl} replace />;
    }
    // If we're on login/register page and just authenticated, check if we came from somewhere
    // This handles Safari private browsing where the redirect might be in the URL but
    // searchParams hasn't updated yet
    if (location.pathname === '/login' || location.pathname === '/register') {
      const fullSearch = location.search;
      const match = fullSearch.match(/redirect=([^&]+)/);
      if (match) {
        return <Navigate to={decodeURIComponent(match[1])} replace />;
      }
    }
    return <Navigate to="/dashboard" replace />;
  }

  // In the native app, skip the marketing homepage — there's no discovery
  // need once someone's already installed the app, so send them straight
  // to sign-in. Web (picknpray.com) keeps the marketing page as-is.
  if (Capacitor.isNativePlatform() && location.pathname === '/') {
    return <Navigate to="/login" replace />;
  }

  return children;
}


function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<PublicRoute><Home /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />

      {/* Join pool - public (shows preview, prompts login to join) */}
      <Route path="/join/:code" element={<JoinPool />} />
      <Route path="/join" element={<JoinPool />} />

      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/create-pool" element={<ProtectedRoute><CreatePool /></ProtectedRoute>} />
      <Route path="/live" element={<ProtectedRoute><LiveLeaderboard /></ProtectedRoute>} />
      <Route path="/pool/:id" element={<ProtectedRoute><PoolDetail /></ProtectedRoute>} />
      <Route path="/pool/:id/picks" element={<ProtectedRoute><PoolPicks /></ProtectedRoute>} />
      <Route path="/pool/:id/leaderboard" element={<ProtectedRoute><PoolLeaderboard /></ProtectedRoute>} />
      <Route path="/pool/:id/settings" element={<ProtectedRoute><PoolSettings /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/badges" element={<ProtectedRoute><Badges /></ProtectedRoute>} />
      <Route path="/lock-sheet" element={<ProtectedRoute><LockSheet /></ProtectedRoute>} />
      <Route path="/corner-chat" element={<ProtectedRoute><CornerChat /></ProtectedRoute>} />
      <Route path="/picks-preview" element={<Navigate to="/lock-sheet" replace />} />

      {/* Admin has its own auth */}
      <Route path="/admin" element={<Admin />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppFooter() {
  const { isAuthenticated } = useAuth();
  return (
    <footer className={isAuthenticated ? 'authenticated-footer' : ''} style={{
      textAlign: 'center',
      padding: '20px',
      background: 'var(--bg-dark)',
      borderTop: '1px solid var(--border)',
      fontSize: '14px',
      color: 'var(--text-muted)'
    }}>
      <div>Pick 'N Pray - Create Your Own Pools</div>
      <div style={{ marginTop: '6px', fontSize: '12px' }}>
        Private group pick'em for entertainment only. Not affiliated with UFC.
      </div>
      <div style={{ marginTop: '8px', fontSize: '12px' }}>
        <Link to="/terms" style={{ color: 'var(--text-muted)' }}>Terms</Link>
        {' · '}
        <Link to="/privacy" style={{ color: 'var(--text-muted)' }}>Privacy</Link>
      </div>
      <div style={{ marginTop: '8px', fontSize: '12px' }}>
        Questions? <a href="mailto:kyle@picknpray.com" style={{ color: 'var(--primary)' }}>kyle@picknpray.com</a>
      </div>
    </footer>
  );
}

// Logged-out visitors on the web get the marketing frame (MarketingNav +
// MarketingFooter) on /, /faq, /terms and /privacy; everyone else, and the
// native apps, get the app shell with the sidebar / tab bar.
function AppShell() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated && !Capacitor.isNativePlatform() && isMarketingPath(location.pathname)) {
    return (
      <>
        <OfflineBanner />
        <MarketingShell>
          <AppRoutes />
        </MarketingShell>
        <Analytics />
      </>
    );
  }
  return (
    <div className="App">
      <OfflineBanner />
      <PushBootstrapper />
      <Sidebar />
      <Navbar />
      <main style={{ flex: 1, paddingBottom: '40px' }}>
        <AppRoutes />
      </main>
      <BottomTabBar />
      <AppFooter />
      <Analytics />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <PoolProvider>
          <Router>
            <AppShell />
          </Router>
        </PoolProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
